import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Star,
  MoreVertical,
  Edit,
  Eye,
  Copy,
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  User,
  Archive,
  Trash2,
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { useFormatting } from '../../hooks/useFormatting';
import { ComprehensiveScenario } from '../../types/scenarios';
import { cn } from '../../lib/utils';
import { ImpactHistoryIcon } from './ImpactHistoryIcon';
import { getCashDurationShortTerm, getCashDurationTerm } from '../../lib/terminology-config';
import { useScenarios } from '../../hooks/useScenarios';
import { useApp } from '../../contexts/AppContext';
import { getRoleTemplateId } from '../../lib/permissions';
import { ScenarioWorkflowActions, ScenarioStatusBadge } from './ScenarioWorkflowActions';
import {
  DeleteErrorModal,
  DeleteConfirmModal,
  ParkDraftModal,
  ParkActiveModal,
} from './ScenarioActionModals';

interface ScenarioCardProps {
  scenario: ComprehensiveScenario;
  onSelect: (id: string) => void;
  onEdit?: (id: string) => void;
  onCompare?: (id: string) => void;
  onClone?: (id: string) => void;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  onSelect,
  onEdit,
  onCompare,
  onClone,
}) => {
  // Base Forecast must never appear as a regular ScenarioCard — defensive guard
  if (scenario.id === 'base') return null;

  const { toggleFavorite, deleteScenario, parkScenario } = useScenarios();
  const { currentUser } = useApp();
  const { formatCurrency } = useFormatting();

  // Modal states
  const [deleteErrorModal, setDeleteErrorModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [parkDraftModal, setParkDraftModal] = useState(false);
  const [parkActiveModal, setParkActiveModal] = useState(false);

  // Get dynamic terminology
  const [metricLabel, setMetricLabel] = useState<string>('Runway');

  useEffect(() => {
    const updateTerminology = () => {
      const shortTerm = getCashDurationShortTerm();
      const label = shortTerm.charAt(0).toUpperCase() + shortTerm.slice(1);
      setMetricLabel(label);
    };

    updateTerminology();

    // Listen for terminology updates
    window.addEventListener('terminology-updated', updateTerminology);
    return () => window.removeEventListener('terminology-updated', updateTerminology);
  }, []);

  // Determine scenario category based on explicit category or changes and metrics
  const getScenarioCategory = () => {
    // If scenario has an explicit category set, use it
    if (scenario.category) {
      return scenario.category;
    }

    // Otherwise, calculate based on changes and metrics
    const hasRevenue = scenario.changes.some(c => 
      c.type === 'win-customer' || c.type === 'add-revenue'
    );
    const hasCostReductions = scenario.changes.some(c => 
      c.type === 'change-costs' && c.amount > 0 // Positive amount = cost reduction/savings
    );
    const hasCostIncreases = scenario.changes.some(c => 
      c.type === 'change-costs' && c.amount < 0 // Negative amount = cost increase
    );
    const hasNegativeImpact = scenario.metrics.netImpact < 0;
    const isUrgent = scenario.priority === 'critical' || scenario.priority === 'high';
    const netImpact = scenario.metrics.netImpact;

    // Crisis: negative impact + urgent priority
    if (hasNegativeImpact && isUrgent) return 'crisis';
    
    // Growth: has revenue OR positive net impact with cost increases (investment scenarios)
    if (hasRevenue || (netImpact > 0 && hasCostIncreases)) return 'growth';
    
    // Cost-Cutting: has cost reductions OR focuses on reducing costs
    if (hasCostReductions || (netImpact > 0 && !hasRevenue && !hasCostIncreases)) return 'cost-cutting';
    
    // Custom: everything else (base forecasts, etc.)
    return 'custom';
  };

  const category = getScenarioCategory();

  // Category-based styling — borders use --mc-* tokens to match dashboard metric cards exactly
  const categoryStyles = {
    growth: {
      borderVar: 'var(--mc-success-border)',
      badge: 'bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-500)]',
      icon: 'text-[var(--accent-success-500)]',
      gradient: 'from-[var(--accent-success-50)]/50 to-transparent',
    },
    'cost-cutting': {
      borderVar: 'var(--mc-info-border)',
      badge: 'bg-[var(--accent-info-50)] text-[var(--accent-info-900)] border-[var(--accent-info-500)]',
      icon: 'text-[var(--accent-info-500)]',
      gradient: 'from-[var(--accent-info-50)]/50 to-transparent',
    },
    crisis: {
      borderVar: 'var(--mc-error-border)',
      badge: 'bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-500)]',
      icon: 'text-[var(--accent-destructive-500)]',
      gradient: 'from-[var(--accent-destructive-50)]/50 to-transparent',
    },
    custom: {
      borderVar: 'var(--mc-warning-border)',
      badge: 'bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-500)]',
      icon: 'text-[var(--accent-warning-500)]',
      gradient: 'from-[var(--accent-warning-50)]/50 to-transparent',
    },
  };

  const style = categoryStyles[category];

  // Health indicator based on metrics (minCash thresholds)
  const getHealthIndicator = () => {
    const minCash = scenario.metrics.minCash;

    if (minCash < 50000) {
      return { color: 'bg-[var(--accent-destructive-500)]', label: 'High Risk' };
    }
    if (minCash >= 50000 && minCash < 100000) {
      return { color: 'bg-[var(--accent-warning-500)]', label: 'Moderate' };
    }
    return { color: 'bg-[var(--accent-success-500)]', label: 'Healthy' };
  };

  const health = getHealthIndicator();

  // Format impact message
  const getImpactMessage = () => {
    const impact = scenario.metrics.netImpact;
    const runway = scenario.metrics.runway;
    
    if (impact > 0) {
      return `Cash impact: +${formatCurrency(impact)} over 90 days`;
    } else if (impact < 0) {
      return `Cash impact: ${formatCurrency(impact)} over 90 days`;
    }
    return `Extends ${getCashDurationShortTerm()} to ${getCashDurationTerm(runway)}`;
  };

  const categoryLabels = {
    growth: 'Growth',
    'cost-cutting': 'Cost-Cutting',
    crisis: 'Crisis',
    custom: 'Custom',
  };

  // Permission-based checks from currentUserPermission
  const permission = scenario.currentUserPermission;
  const hasEditPermission = permission === 'owner' || permission === 'edit';
  const hasOwnerPermission = permission === 'owner';

  // Check if user can edit (must have edit permission AND status allows it)
  const canEdit = () => {
    if (!hasEditPermission) return false;
    // Active scenarios can only be edited by CFO, executive, or system admin
    if (scenario.status === 'active') {
      const roleId = getRoleTemplateId(currentUser.forecasterRole);
      return roleId === 'cfo' || roleId === 'executive' || roleId === 'system_admin';
    }
    return true;
  };

  // Handle Edit action
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit()) return;
    if (onEdit) {
      onEdit(scenario.id);
    }
  };

  // Handle Delete action
  const handleDelete = () => {
    // Cannot delete active scenarios
    if (scenario.status === 'active') {
      setDeleteErrorModal(true);
      return;
    }
    
    // Can delete if not active
    setDeleteConfirmModal(true);
  };

  const confirmDelete = () => {
    deleteScenario(scenario.id);
    setDeleteConfirmModal(false);
  };

  // Handle Park action — available from any non-parked status
  const handlePark = () => {
    if (scenario.status === 'parked') return;
    
    // Active scenarios get a warning modal; all others use the standard draft modal
    if (scenario.status === 'active') {
      setParkActiveModal(true);
    } else {
      // draft, under-review, pending-approval, rejected — all use the same modal
      setParkDraftModal(true);
    }
  };

  const confirmParkDraft = () => {
    const reviewDate = new Date();
    reviewDate.setMonth(reviewDate.getMonth() + 3); // 3 months from now
    parkScenario(scenario.id, 'Parked by user', reviewDate);
    setParkDraftModal(false);
  };

  const confirmParkActive = () => {
    const reviewDate = new Date();
    reviewDate.setMonth(reviewDate.getMonth() + 3);
    parkScenario(scenario.id, 'Active scenario parked by user', reviewDate);
    setParkActiveModal(false);
  };



  // Handle Clone action
  const handleClone = () => {
    if (onClone) {
      onClone(scenario.id);
    }
  };

  // Handle Compare action
  const handleCompare = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCompare) {
      onCompare(scenario.id);
    }
  };

  // Handle Favorite toggle
  const handleToggleFavorite = () => {
    toggleFavorite(scenario.id);
  };

  return (
    <>
      <Card
        className="group relative overflow-hidden"
        style={{ borderLeft: `3px solid ${style.borderVar}` }}
      >

        
        {/* Dropdown menu — outside clickable area so it never triggers onSelect */}
        <div
          className="absolute top-3 right-3 z-30"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onSelect(scenario.id)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(scenario.id)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {onCompare && (
                <DropdownMenuItem onClick={() => onCompare(scenario.id)}>
                  <GitCompare className="h-4 w-4 mr-2" />
                  Compare
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleClone()}>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleToggleFavorite()}>
                <Star className="h-4 w-4 mr-2" />
                {scenario.isFavorite ? 'Remove from' : 'Add to'} Favourites
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {hasEditPermission && scenario.status !== 'parked' && (
                <DropdownMenuItem onClick={() => handlePark()}>
                  <Archive className="h-4 w-4 mr-2" />
                  Park
                </DropdownMenuItem>
              )}
              {hasOwnerPermission && (scenario.status === 'draft' || scenario.status === 'parked') && (
                <DropdownMenuItem onClick={() => handleDelete()} className="text-neutral-700">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Clickable content area */}
        <CardContent className="relative p-6 cursor-pointer" onClick={() => onSelect(scenario.id)}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className={cn('font-medium', style.badge)}>
                  {categoryLabels[category]}
                </Badge>
                {scenario.isFavorite && (
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1 truncate">
                {scenario.name}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {scenario.description}
              </p>
            </div>
          </div>

          {/* Key Impact Metric */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {scenario.metrics.netImpact > 0 ? (
                  <TrendingUp className={cn('h-5 w-5', style.icon)} />
                ) : scenario.metrics.netImpact < 0 ? (
                  <TrendingDown className="h-5 w-5 text-[var(--accent-destructive-500)]" />
                ) : (
                  <Minus className="h-5 w-5 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700">
                  {getImpactMessage()}
                </span>
                {scenario.impactHistory && scenario.impactHistory.length > 0 && (
                  <ImpactHistoryIcon
                    history={scenario.impactHistory}
                    title={`Impact History — ${scenario.name}`}
                  />
                )}
              </div>
              {/* Health Indicator */}
              <div className="flex items-center gap-2">
                <div className={cn('w-2 h-2 rounded-full', health.color)} />
                <span className="text-xs text-gray-500">{health.label}</span>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Min Cash</div>
              <div className="text-sm font-semibold text-gray-900">
                {formatCurrency(scenario.metrics.minCash)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">{metricLabel}</div>
              <div className="text-sm font-semibold text-gray-900">
                {scenario.metrics.runway} mo
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Changes</div>
              <div className="text-sm font-semibold text-gray-900">
                {scenario.changes.length}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{scenario.createdByName}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{formatDate(scenario.modifiedAt)}</span>
              </div>
            </div>

            {/* Status Badge */}
            <ScenarioStatusBadge status={scenario.status} />
          </div>

        </CardContent>

        {/* Workflow Actions — rendered outside CardContent so the hover overlay never covers them */}
        <div className="relative z-10 bg-white px-6 pb-6 pt-3 border-t border-gray-100">
          <ScenarioWorkflowActions scenario={scenario} variant="card" />
        </div>
      </Card>

      {/* Modals */}
      <DeleteErrorModal
        open={deleteErrorModal}
        onClose={() => setDeleteErrorModal(false)}
        status={scenario.status}
        scenarioName={scenario.name}
      />
      <DeleteConfirmModal
        open={deleteConfirmModal}
        onClose={() => setDeleteConfirmModal(false)}
        onConfirm={confirmDelete}
        scenarioName={scenario.name}
      />
      <ParkDraftModal
        open={parkDraftModal}
        onClose={() => setParkDraftModal(false)}
        onConfirm={confirmParkDraft}
        scenarioName={scenario.name}
      />
      <ParkActiveModal
        open={parkActiveModal}
        onClose={() => setParkActiveModal(false)}
        onConfirm={confirmParkActive}
        scenarioName={scenario.name}
      />

    </>
  );
};
